import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { gateOnboardingIncomplete, sessionQuery } from '@/lib/use-session';
import { redirectToSignIn } from '@/lib/route-guards';
import { SessionErrorPage } from '@/components/shell/SessionErrorPage';

// Auth gate for the whole onboarding flow. Onboarding is reached via
// `/auth/finishing`, which only routes here once Clerk has a live session —
// but the steps call authed endpoints (`/me/context`, photo upload, places),
// so a visitor who lands here without a session (direct nav, expired session)
// would see every call 401. Mirror the `_authed` guard: resolve the session
// and bounce unauthenticated visitors to `/sign-in`.
//
// Same error classification as `_authed`: a NULL probe is the definitive
// signed-out verdict → sign-in; a THROWING probe (network failure, 5xx) means
// the auth state is unknown → it propagates to `errorComponent`, a retryable
// surface, instead of being misread as a sign-out.
export const Route = createFileRoute('/onboarding')({
  beforeLoad: async ({ context }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionQuery);
    // No `next`: an onboarding step isn't a destination of its own. Signing
    // in resumes onboarding through /auth/finishing anyway, and a stored
    // /onboarding/* landing would bounce a just-onboarded member to /feed.
    if (ctx === null) throw redirectToSignIn();
    // Deactivated members can't complete onboarding (every write 403s
    // `identity/deactivated`) — send them to /feed, where the `_authed`
    // shell renders the reactivation prompt instead.
    if (ctx.member.deactivatedAt !== null) {
      throw redirect({ to: '/feed' });
    }
    // Already finished onboarding? Don't show the flow again — bounce to
    // /feed. Inverse of the `_authed`/`_public` gate; together they make
    // routing self-correcting, so a stale session cache or a back-button
    // can't strand a completed user on the profile step. Safe mid-tour:
    // completion is stamped only on a step's final-screen click, which
    // navigates out to /feed — this never fires between tour steps.
    gateOnboardingIncomplete(ctx);
    return { session: ctx.session, member: ctx.member };
  },
  errorComponent: SessionErrorPage,
  component: OnboardingLayout,
});

// Each step renders its own `OnboardingShell`; this layout is just the
// guarded outlet.
function OnboardingLayout() {
  return <Outlet />;
}
